/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxiosForClient } from '@/lib/axios';
import {
  type QueryClient,
  type QueryFunctionContext,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import type { FormattedContentStackHomepage } from '@/features/contentstack/interface';
import { getContentStackHomePageContent } from '@/features/contentstack/lib/home/getContentStackHomePageContent'; // eslint-disable-line no-restricted-imports
import { type PageContextType } from '@/next-types';
import { useSegments } from '@/features/header/api/getSegments';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';
import { getKiboDecisionFlag, useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';

const URL = `/api/contentstack/home`;

const HOMEPAGE_VARIANT_SEGMENT = 'homepageVariant';
const HOMEPAGE_CONTROL_SEGMENT = 'homepageControl';

const getHomePageContent = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getHomePageKey>>) => {
  const [, , { segments }] = queryKey;
  const response = await getAxiosForClient().get<FormattedContentStackHomepage>(URL, {
    params: {
      segments: segments.join(','),
    },
  });
  return selector(response.data);
};

const selector = (data: FormattedContentStackHomepage) => data;

export const getHomePageKey = (segments: string[]) => ['cms', 'home', { segments }] as const;

export const useHomePageContent = (
  options: Omit<
    UseQueryOptions<
      FormattedContentStackHomepage,
      unknown,
      FormattedContentStackHomepage,
      ReturnType<typeof getHomePageKey>
    >,
    'queryKey' | 'queryFn'
  > = {},
) => {
  const { data: segments, isSuccess } = useSegments();
  const locale = useLocale();
  const isHomepageVariant = useMonetateDecisionFlag('homepageVariant');
  const allSegments = [
    ...(segments ?? []),
    isHomepageVariant ? HOMEPAGE_VARIANT_SEGMENT : HOMEPAGE_CONTROL_SEGMENT,
  ].sort();
  return useQuery({
    queryKey: getHomePageKey(allSegments),
    queryFn: getHomePageContent,
    enabled: (options.enabled ?? true) && (isSuccess || locale === countryCodes.ptBr),
  });
};

export const prefetchHomePageContent = (
  queryClient: QueryClient,
  context: PageContextType,
  userSegments: string[],
) => {
  const isHomepageVariant = getKiboDecisionFlag(queryClient, 'homepageVariant');
  const allSegments = [
    ...userSegments,
    isHomepageVariant ? HOMEPAGE_VARIANT_SEGMENT : HOMEPAGE_CONTROL_SEGMENT,
  ].sort();
  return queryClient.prefetchQuery({
    queryKey: getHomePageKey(allSegments),
    queryFn: () => getContentStackHomePageContent(context.req, context.query, allSegments),
  });
};
